import { useCallback, useEffect, useState } from 'react';
import { useAccountApi } from '../../api/AccountApiProvider';
import { toApiError } from '../../api/errors';
import type { ApiError } from '../../api/errors';
import { watchIdentity } from '../../api/watch';
import { SIGNING_ALGS, providerChoice } from '../../api/types';
import type { IdentityChoice, SigningAlg, TenancyIdentity } from '../../api/types';
import { useI18n } from '../../i18n/useI18n';
import { Alert, ErrorAlert } from '../../ui/Alert';
import { Button, Card, Loading, PageHead } from '../../ui/primitives';
import { Modal } from '../../ui/Modal';
import { RedirectUriNotice } from '../../ui/RedirectUriNotice';
import { TextField } from '../../ui/form';
import { IdentityStateBadge } from '../../ui/StatusBadge';
import { IconExternal } from '../../ui/icons';
import { USING_STUBS } from '../../api';
import { StubIdentityProviderHint } from '../../stubs/StubBanner';
import { IdentityProviderSummary } from './IdentityProviderSummary';
import { SwitchIdentityProviderDialog } from './SwitchIdentityProviderDialog';
import {
  checkOwnProvider,
  desiredDiffersFromApplied,
  failureKey,
  formFor,
  isFailureState,
  ownChoice,
  providerOption,
  stateKey,
} from './identityChoice';
import type { OwnProviderErrors, OwnProviderForm } from './identityChoice';

/**
 * Where a tenancy decides how its people sign in to its cluster.
 *
 * This screen lives in the account, on the provider's domain, and it has to:
 * the failure it exists to recover from is a tenancy that pointed its cluster
 * at a provider that does not work and can no longer reach the cluster console
 * to undo it. A way back that lived in the cluster console would be a way back
 * that is gone exactly when it is needed.
 *
 * **Choosing is a modal, and being told is the page.** Six fields and two
 * priced options used to stand on the screen at all times, for a decision made
 * about once a year; what somebody comes here for the rest of the time is to
 * see that sign-in works, to read the return address down a telephone, and — on
 * the day it does not work — to get out. Those three are the page now, and the
 * choice is behind a button.
 *
 * Applying is not a request that returns a result. Declaring a choice starts a
 * change and answers with the state it started in; the screen then re-reads the
 * same block until it stops running. That is the server's shape and it is the
 * right one — a restart of the tenancy's control plane is not something to hold
 * an HTTP request open across, and a browser that gave up would leave the
 * operation running with nobody watching.
 */
export function IdentityProviderScreen() {
  const { t } = useI18n();
  const api = useAccountApi();

  const [status, setStatus] = useState<TenancyIdentity | null>(null);
  const [loadFailure, setLoadFailure] = useState<ApiError | null>(null);
  const [gaveUp, setGaveUp] = useState(false);
  const [reads, setReads] = useState(0);

  /** The half-made choice, while somebody is making it. */
  const [choosing, setChoosing] = useState<ChoiceState | null>(null);
  /** What they asked to apply, waiting to be confirmed. */
  const [confirming, setConfirming] = useState<IdentityChoice | null>(null);

  const reload = useCallback(() => {
    setReads((n) => n + 1);
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    api.tenancy.getIdentity(controller.signal).then(
      (value) => {
        if (controller.signal.aborted) return;
        setStatus(value);
        setLoadFailure(null);
        setGaveUp(false);
      },
      (error: unknown) => {
        if (controller.signal.aborted) return;
        setLoadFailure(toApiError(error));
      },
    );
    return () => {
      controller.abort();
    };
  }, [api, reads]);

  // While a change is running there is nothing to do but watch it, so the
  // screen watches rather than leaving a page that stopped being true.
  const running = status?.in_flight === true;
  useEffect(() => {
    if (!running) return;
    const controller = new AbortController();

    void watchIdentity(
      (signal) => api.tenancy.getIdentity(signal),
      (next) => {
        setStatus(next);
      },
      { signal: controller.signal },
    ).then((outcome) => {
      if (controller.signal.aborted) return;
      if (outcome.done === 'failed') setLoadFailure(toApiError(outcome.error));
      if (outcome.done === 'gave_up') setGaveUp(true);
    });

    return () => {
      controller.abort();
    };
  }, [api, running]);

  return (
    <div className="content-inner">
      <PageHead
        title={t('identityProvider.title')}
        {...(status === null
          ? {}
          : {
              actions: (
                <Button
                  variant="primary"
                  disabled={status.in_flight}
                  onClick={() => {
                    setChoosing(openOn(status));
                  }}
                >
                  {t('identityProvider.change')}
                </Button>
              ),
            })}
      />

      {status === null && loadFailure === null && <Loading />}

      {status === null && loadFailure !== null && <LoadFailure failure={loadFailure} onRetry={reload} />}

      {status !== null && (
        <div className="stack">
          <Working status={status} />

          {loadFailure !== null && <ErrorAlert error={loadFailure} />}

          {gaveUp && (
            <Alert tone="warn" title={t('identityProvider.watch.gaveUp.title')} live>
              <p>{t('identityProvider.watch.gaveUp.body')}</p>
              <div style={{ marginTop: 12 }}>
                <Button size="sm" onClick={reload}>
                  {t('common.retry')}
                </Button>
              </div>
            </Alert>
          )}

          <ChangePanel status={status} onChanged={reload} />

          {/* Dictated down a telephone more often than anything else here: it is
              registered at somebody else's provider, byte for byte, and nothing
              on this side can check that it was. */}
          <RedirectUriNotice value={status.redirect_uri} />

          <InUse status={status} />

          {USING_STUBS && <StubIdentityProviderHint onChanged={reload} />}
        </div>
      )}

      {choosing !== null && status !== null && (
        <ChooseProviderDialog
          status={status}
          choice={choosing}
          onChoice={setChoosing}
          onClose={() => {
            setChoosing(null);
          }}
          onSubmit={(chosen) => {
            setChoosing(null);
            setConfirming(chosen);
          }}
        />
      )}

      {confirming !== null && status !== null && (
        <SwitchIdentityProviderDialog
          choice={confirming}
          status={status}
          onClose={() => {
            // Back to the fields as they were left: a confirmation somebody
            // stepped out of must not cost them the six values they typed.
            setConfirming(null);
            setChoosing(openOn(status, confirming));
          }}
          onStarted={(next) => {
            setConfirming(null);
            setStatus(next);
          }}
        />
      )}
    </div>
  );
}

/** The choice as it stands while somebody is making it. */
interface ChoiceState {
  kind: 'provider' | 'own';
  form: OwnProviderForm;
  errors: OwnProviderErrors;
}

/** The dialog opens on what the tenancy last asked for, not on an empty form. */
function openOn(status: TenancyIdentity, from?: IdentityChoice): ChoiceState {
  const start = from ?? status.desired;
  const option = providerOption(status);
  return {
    kind: start.kind === 'own' || !option.available ? 'own' : 'provider',
    form: formFor(start),
    errors: {},
  };
}

/**
 * The one refusal worth its own words on the way in: an account that represents
 * no tenancy, or more than one, cannot be asked this question — choosing for it
 * would be choosing for it.
 */
function LoadFailure({ failure, onRetry }: { failure: ApiError; onRetry: () => void }) {
  const { t } = useI18n();

  if (failure.code === 'conflict') {
    return (
      <Alert tone="warn" title={t('identityProvider.notSole.title')} live>
        {t('identityProvider.notSole.body')}
      </Alert>
    );
  }
  return (
    <ErrorAlert
      error={failure}
      action={
        <div style={{ marginTop: 12 }}>
          <Button size="sm" onClick={onRetry}>
            {t('common.retry')}
          </Button>
        </div>
      }
    />
  );
}

/**
 * Who is issuing the tokens this cluster trusts, in one line.
 *
 * A cluster with nothing applied is the exception and gets a band, because it
 * is a state that is wrong now: the cluster is up and its console lets nobody
 * in at all.
 */
function Working({ status }: { status: TenancyIdentity }) {
  const { t } = useI18n();

  const applied = status.applied;
  if (applied === undefined) {
    return (
      <Alert tone="warn" title={t(stateKey(status.state))}>
        <p>{t('identityProvider.none')}</p>
      </Alert>
    );
  }

  const issuer =
    applied.kind === 'provider'
      ? (status.provider_issuer ?? t('identityProvider.issuer.unnamed'))
      : applied.issuer;

  return (
    <div className="state-line">
      <IdentityStateBadge state={status.state} />
      <span className="faint">{t('identityProvider.running')}</span>
      <span className="mono">{issuer}</span>
      <span className="sep" aria-hidden="true">
        ·
      </span>
      <span>
        {t(
          applied.kind === 'provider'
            ? 'identityProvider.kind.provider'
            : 'identityProvider.kind.own',
        )}
      </span>
    </div>
  );
}

/**
 * Where the last change got to, and the ways out of the two states that need
 * one. Both ways out live here, in the account, on purpose.
 *
 * It stands only while something is running or something went wrong. A change
 * that landed is the ordinary state of this screen and is said by the badge on
 * the line above; a band announcing it would be a band nobody reads on the day
 * one matters.
 */
function ChangePanel({ status, onChanged }: { status: TenancyIdentity; onChanged: () => void }) {
  const { t, formatRelative } = useI18n();
  const api = useAccountApi();

  const [failure, setFailure] = useState<ApiError | null>(null);
  const [busy, setBusy] = useState(false);

  const failed = isFailureState(status.state);
  if (!failed && !status.in_flight) return null;

  async function run(action: () => Promise<TenancyIdentity>) {
    setFailure(null);
    setBusy(true);
    try {
      await action();
      onChanged();
    } catch (error: unknown) {
      setFailure(toApiError(error));
      // Whatever the refusal was, the truth is on the server: read it again.
      onChanged();
    } finally {
      setBusy(false);
    }
  }

  const startedAt = status.started_at;

  return (
    <Alert tone={failed ? 'deny' : 'warn'} title={t(stateKey(status.state))} live={failed}>
      <div className="row" style={{ marginBottom: 10 }}>
        <IdentityStateBadge state={status.state} />
        {startedAt !== undefined && (
          <span className="faint">
            {t('identityProvider.startedAt', { when: formatRelative(startedAt) })}
          </span>
        )}
      </div>

      {status.in_flight && (
        <>
          <p>{t('identityProvider.applying.body')}</p>
          <p style={{ marginTop: 8 }}>{t('identityProvider.applying.sessions')}</p>
        </>
      )}

      {/* Nine codes, one at a time: which of the provider's answers refused, and
          then what the cluster was left on. */}
      {failed && (
        <>
          <p>{t(failureKey(status.failure))}</p>
          <p style={{ marginTop: 8 }}>
            {status.state === 'rejected'
              ? t('identityProvider.rejected.body')
              : t('identityProvider.failed.body')}
          </p>
        </>
      )}

      {status.detail !== '' && (
        <p className="mono" style={{ marginTop: 8 }}>
          {status.detail}
        </p>
      )}

      {failure !== null && (
        <div style={{ marginTop: 12 }}>
          <ActionFailure failure={failure} />
        </div>
      )}

      {/* Beside the band, because they are what somebody does about it. */}
      {!status.in_flight && (
        <div className="row" style={{ marginTop: 12 }}>
          {failed && (
            <Button
              size="sm"
              disabled={busy}
              onClick={() => void run(() => api.tenancy.applyIdentity())}
            >
              {t('identityProvider.applyAgain')}
            </Button>
          )}
          {failed && (
            <Button
              size="sm"
              disabled={busy}
              onClick={() => void run(() => api.tenancy.dismissFailure())}
            >
              {t('identityProvider.dismiss')}
            </Button>
          )}
          {status.revertable && (
            <Button
              size="sm"
              variant="primary"
              disabled={busy}
              onClick={() => void run(() => api.tenancy.revertIdentity())}
            >
              {t('identityProvider.revert')}
            </Button>
          )}
        </div>
      )}

      {!status.in_flight && !status.revertable && failed && (
        <p className="faint" style={{ marginTop: 10 }}>
          {t('identityProvider.noPrevious')}
        </p>
      )}
    </Alert>
  );
}

/**
 * Two refusals that are not failures of the console and read badly as generic
 * errors: somebody else started a change, and there was nothing to dismiss.
 * Both mean the screen is behind, and both are answered by re-reading — which
 * the caller has already done by the time this is drawn.
 */
function ActionFailure({ failure }: { failure: ApiError }) {
  const { t } = useI18n();

  if (failure.code === 'change_under_way') {
    return (
      <Alert tone="warn" title={t('identityProvider.underWay.title')} live>
        {t('identityProvider.underWay.body')}
      </Alert>
    );
  }
  if (failure.code === 'no_failure_to_dismiss') {
    return (
      <Alert tone="neutral" title={t('identityProvider.nothingToDismiss.title')} live>
        {t('identityProvider.nothingToDismiss.body')}
      </Alert>
    );
  }
  if (failure.code === 'conflict') {
    return (
      <Alert tone="warn" title={t('identityProvider.nowhereBack.title')} live>
        {t('identityProvider.nowhereBack.body')}
      </Alert>
    );
  }
  return <ErrorAlert error={failure} />;
}

/** What the cluster runs, what it has been asked to run instead, and where it is. */
function InUse({ status }: { status: TenancyIdentity }) {
  const { t } = useI18n();

  const tenancy = status.tenancy;
  const pendingChange = status.applied === undefined || desiredDiffersFromApplied(status);
  const reachable = status.applied !== undefined && !status.in_flight;

  return (
    <Card>
      <div className="card-head">
        <span className="ct">{t('identityProvider.inUse.title')}</span>
      </div>
      <div className="card-pad">
        <IdentityProviderSummary choice={status.applied} providerIssuer={status.provider_issuer} />

        {/* Declared and not running is a state that is wrong now: what the
            cluster enforces is not what the tenancy asked for. */}
        {pendingChange && status.applied !== undefined && (
          <div style={{ marginTop: 18 }}>
            <Alert tone="warn" title={t('identityProvider.declared.title')}>
              <p>{t('identityProvider.declared.note')}</p>
            </Alert>
            <div style={{ marginTop: 12 }}>
              <IdentityProviderSummary
                choice={status.desired}
                providerIssuer={status.provider_issuer}
              />
            </div>
          </div>
        )}

        <dl className="dl" style={{ marginTop: 18 }}>
          <dt>{t('tenant.field.consoleDomain')}</dt>
          <dd className="mono">{tenancy.console_domain}</dd>
          <dt>{t('tenant.field.gatewayDomain')}</dt>
          <dd className="mono">{tenancy.gateway_domain}</dd>
        </dl>

        {reachable && (
          <div style={{ marginTop: 14 }}>
            <a className="btn btn-ghost btn-sm" href={`https://${tenancy.console_domain}/`}>
              <IconExternal size={15} />
              {t('identityProvider.openConsole')}
            </a>
          </div>
        )}
      </div>
    </Card>
  );
}

/**
 * The choice itself, with the price of each option beside the option rather
 * than in a footnote under the button.
 *
 * A dialog, because it is an event: a tenancy changes this about once, and
 * fields that stand open all year are fields somebody edits by accident. The
 * requirements of a provider live in here too — they are read while typing into
 * these boxes and at no other time.
 */
function ChooseProviderDialog({
  status,
  choice,
  onChoice,
  onSubmit,
  onClose,
}: {
  status: TenancyIdentity;
  choice: ChoiceState;
  onChoice: (next: ChoiceState) => void;
  onSubmit: (chosen: IdentityChoice) => void;
  onClose: () => void;
}) {
  const { t } = useI18n();

  const option = providerOption(status);

  function submit() {
    if (choice.kind === 'provider') {
      onChoice({ ...choice, errors: {} });
      onSubmit(providerChoice());
      return;
    }
    const found = checkOwnProvider(choice.form);
    onChoice({ ...choice, errors: found });
    if (Object.keys(found).length > 0) return;
    onSubmit(ownChoice(choice.form));
  }

  return (
    <Modal
      title={t('identityProvider.change')}
      subtitle={t('identityProvider.choose.legend')}
      onClose={onClose}
      footer={
        <>
          <Button variant="primary" onClick={submit}>
            {t('identityProvider.submit')}
          </Button>
          <Button onClick={onClose}>{t('common.cancel')}</Button>
        </>
      }
    >
      <div className="choices">
        <label
          className="choice"
          data-selected={choice.kind === 'provider'}
          data-unavailable={!option.available}
        >
          <input
            type="radio"
            name="identity-provider-kind"
            checked={choice.kind === 'provider'}
            disabled={!option.available}
            onChange={() => {
              onChoice({ ...choice, kind: 'provider' });
            }}
          />
          <span>
            <span className="ch-title">{t('identityProvider.kind.provider')}</span>
            {option.available ? (
              <>
                <span className="ch-body">
                  {t('identityProvider.kind.provider.body', { issuer: option.issuer })}
                </span>
                <span className="ch-price">
                  <span className="ch-price-title">{t('identityProvider.price.title')}</span>
                  {t('identityProvider.price.provider')}
                </span>
              </>
            ) : (
              <span className="ch-body">{t(option.reason)}</span>
            )}
          </span>
        </label>

        <label className="choice" data-selected={choice.kind === 'own'}>
          <input
            type="radio"
            name="identity-provider-kind"
            checked={choice.kind === 'own'}
            onChange={() => {
              onChoice({ ...choice, kind: 'own' });
            }}
          />
          <span>
            <span className="ch-title">{t('identityProvider.kind.own')}</span>
            <span className="ch-body">{t('identityProvider.kind.own.body')}</span>
            <span className="ch-price">
              <span className="ch-price-title">{t('identityProvider.price.title')}</span>
              {t('identityProvider.price.own')}
            </span>
          </span>
        </label>
      </div>

      {choice.kind === 'own' && (
        <OwnProviderFields
          form={choice.form}
          errors={choice.errors}
          onChange={(form) => {
            onChoice({ ...choice, form });
          }}
        />
      )}

      <div style={{ marginTop: 'var(--sp-5)' }}>
        <Alert tone="warn" title={t('identityProvider.effect.title')}>
          <p>{t('identityProvider.effect.restart')}</p>
          <p style={{ marginTop: 8 }}>{t('identityProvider.effect.pause')}</p>
          <p style={{ marginTop: 8 }}>{t('identityProvider.effect.sessions')}</p>
        </Alert>
      </div>
    </Modal>
  );
}

function OwnProviderFields({
  form,
  errors,
  onChange,
}: {
  form: OwnProviderForm;
  errors: OwnProviderErrors;
  onChange: (next: OwnProviderForm) => void;
}) {
  const { t } = useI18n();

  return (
    <div style={{ marginTop: 'var(--sp-5)' }}>
      <p className="section-title" style={{ marginTop: 0 }}>
        {t('identityProvider.own.section')}
      </p>

      <TextField
        label={t('identityProvider.field.issuer')}
        hint={t('identityProvider.issuer.hint')}
        placeholder={t('identityProvider.issuer.placeholder')}
        type="url"
        mono
        value={form.issuer}
        onChange={(event) => {
          onChange({ ...form, issuer: event.target.value });
        }}
        {...(errors.issuer === undefined ? {} : { error: t(errors.issuer) })}
      />
      <TextField
        label={t('identityProvider.field.clientId')}
        hint={t('identityProvider.clientId.hint')}
        placeholder={t('identityProvider.clientId.placeholder')}
        mono
        value={form.client_id}
        onChange={(event) => {
          onChange({ ...form, client_id: event.target.value });
        }}
        {...(errors.client_id === undefined ? {} : { error: t(errors.client_id) })}
      />
      <TextField
        label={t('identityProvider.field.audience')}
        hint={t('identityProvider.audience.hint')}
        mono
        value={form.audience}
        onChange={(event) => {
          onChange({ ...form, audience: event.target.value });
        }}
        {...(errors.audience === undefined ? {} : { error: t(errors.audience) })}
      />

      <fieldset>
        <legend className="fieldset-legend">{t('identityProvider.field.signingAlgs')}</legend>
        <div className="alg-list">
          {SIGNING_ALGS.map((alg: SigningAlg) => (
            <label className="alg" key={alg}>
              <input
                type="checkbox"
                checked={form.signing_algs.includes(alg)}
                onChange={(event) => {
                  onChange({
                    ...form,
                    signing_algs: event.target.checked
                      ? [...form.signing_algs, alg]
                      : form.signing_algs.filter((one) => one !== alg),
                  });
                }}
              />
              <span className="mono">{alg}</span>
            </label>
          ))}
        </div>
        <p className="hint">{t('identityProvider.signingAlgs.hint')}</p>
      </fieldset>

    </div>
  );
}
